import { Component } from "@omnia/shared/shell/component"
import { TargetNutrients } from "../../../analysis/targetNutrients"
import { defineComponent, IOmniaShell, IParam, isArray, param, shell } from "../../../omnia"
import { getManureTypeApp } from "../../../services/manureAnalysis"
import { getProductUnits, getProductUnitsText, getProductUnitsTextNormalized } from "../../../services/units"
import { StockVm } from "../../../stocks/stockVm"
import { FertiliserAppUnit, INutritionPlanProductApp, INutritionService, NutrientType } from "../../../types"
import { LimeEnabledFromYear } from "../../../utils"
import { PlanEditCtrl } from "../edit"
import { IPlanEditContext } from "../types"
import { compareApplicationVms } from "./compare"
import { ProductApplicationVm } from "./productApplicationVm"
import { TemplatesCtrl } from "./templates"
import { isNumber } from "@omnia/shared/utils/utils"
import { getNutrientHorizontalBarBackgroundStyle } from "@omnia/shared/nutrition/colors"

export class PlanProductsEditCtrl extends Component {

    static create(): ng.IDirective {

        var def = defineComponent(PlanProductsEditCtrl, "app/nutrition/plan/edit/products/products.html", {
            showValidationErrors: "=",
            readOnly: "="
        })

        def.require = "^omniaNutriPlanEdit"

        def.link = PlanProductsEditCtrl.link

        return def

    }

    static link(scope, elt, attrs, parent: PlanEditCtrl) {
        var ctrl: PlanProductsEditCtrl = scope["vm"]
        parent.productsEditCtrl = ctrl
    }

    stocksSelectorPopupLeftOffset = -150

    public readOnly: boolean

    private ctxt: IPlanEditContext
    private templatesCtrl: TemplatesCtrl

    private showValidationErrors: boolean

    public products: ProductApplicationVm[]
    public stocks: StockVm[]

    public editedProductBase: ProductApplicationVm
    public editedProduct: ProductApplicationVm
    public isEditing: boolean = false

    private targetNutrients: TargetNutrients

    public manureAppMethods: IParam[]

    static $inject = ["$scope", "OmniaNutritionService", "OmniaShell"]

    constructor($scope: ng.IScope, private svc: INutritionService, private shell: IOmniaShell) {

        super($scope)

        this._icon = "fas fa-pen"
        this._title = "Nutrition plan / edit products"

        this.targetNutrients = new TargetNutrients()

    }

    protected setup() {

        this.ctxt = <IPlanEditContext>this.execArguments

        this.products = this.ctxt.applications
        this.stocks = this.ctxt.stocks

        this.templatesCtrl = new TemplatesCtrl(this.svc, this.ctxt, this.shell)

        this.manureAppMethods = param.ManureAppMethod()

        this.sortApplications()

        if (this.ctxt.args.openFirstApp && (this.products.length > 0)) {
            this.editProductIfPossible(this.products[0])
        }

    }

    public getStocks() {

        let stocks = this.stocks

        if (this.products.length > 1) {

            let product = this.products.filter(p => !!(p.stock))[0]

            if (product.stock.isManure()) {
                stocks = stocks.filter(s => s.isManure())
            }
            else {
                stocks = stocks.filter(s => s.isFertiliser())
            }

        }

        return stocks

    }

    public getVisibleStocks() {

        let stocks = this.getStocks()

        const current = this.editedProduct?.newStock?.stockId

        stocks = stocks.filter(s => (current && (s.stockId === current)) || (!s.hidden))

        return stocks

    }

    public getTargetNutrients(product: ProductApplicationVm) {

        let lime = (this.svc.yearId >= LimeEnabledFromYear)
        return this.targetNutrients.getTargetNutrients(product.analysis, lime)

    }

    public sortApplications() {

        var products = this.products || []

        products = products.sort((a, b) => {

            let d = a.applicationDate > b.applicationDate ? 1 : a.applicationDate < b.applicationDate ? -1 : 0
            d = d || a.stock.description.localeCompare(b.stock.description)
            return d
        })

        this.products = products

    }

    getNutrientStyle(product: ProductApplicationVm, nutrient: string): any {

        if (!product) return

        let stock = product.stock
        if (!stock) return

        var value = stock.getNutrientValue(nutrient)
        var max = product.nutrientsMaxRate

        if (isNumber(value)) {
            return getNutrientHorizontalBarBackgroundStyle(nutrient, value, max)
        }

    }

    hasProducts(): boolean {
        return this.products && (this.products.length > 0)
    }

    canAddProduct(): boolean {
        return (!this.readOnly) && (!this.isEditing) && (!this.ctxt.useBiomass || (this.ctxt.useBiomass && this.products && this.products.length === 0))
    }

    canManageOrSaveTemplates(): boolean {
        return (!this.readOnly) && (!this.isEditing) && (!this.products || this.products.filter(p => p.isManure()).length === 0)
    }

    saveTemplate() {
        this.templatesCtrl.saveTemplate(this.products)
    }

    manageTemplates() {

        this.templatesCtrl.manageTemplates(this.products).then(p => {

            if (!isArray(p)) return

            this.products = p

            this.sortApplications()

            if (this.ctxt.useBiomass && ((this.products.length === 1 && this.products[0].targetNutrient !== NutrientType.N) || this.products.length > 1)) {
                this.ctxt.useBiomass = false
            }

            this.ctxt.updateTargetModel()

        })

    }

    addNewProduct() {

        if (this.canAddProduct()) {

            var newPlanProduct = <INutritionPlanProductApp>{}

            var vm = new ProductApplicationVm(this.ctxt, this.svc, newPlanProduct)
            vm.justAdded = true

            this.products.push(vm)

            this.editProduct(vm)
        }

    }

    private canChangeAttr(): boolean {
        return (!this.readOnly) && (this.isEditing) && (!!this.editedProduct)
    }

    canChangeProduct(): boolean {
        return this.canChangeAttr()
    }

    canChangeTargetNutrient(): boolean {
        return this.canChangeAttr()
    }

    canChangeDate(): boolean {
        return this.canChangeAttr()
    }

    canEditActualData(): boolean {
        return (!this.readOnly) && this.isEditing
    }

    canInputPctRequirements() {
        if (this.readOnly) return false
        if (this.useBiomass) return false
        return true
    }

    hasValidDate() {
        const e = this.editedProduct
        const app = e && e.productApp
        return e && app.ApplicationDate && (!e.dateInvalid)
    }

    hasRate() {
        const e = this.editedProduct
        return e && (e.hasNutrientPct || e.hasNutrientRate || e.hasProductRate)
    }

    get app() {
        return this.editedProduct
    }

    get useBiomass() {
        return this.ctxt && this.ctxt.useBiomass
    }

    get usesFlatProductRate() {
        const e = this.editedProduct
        return e && e.usesFlatProductRate
    }

    get usesManure() {
        return this.products && this.products.some(p => p.isManure())
    }

    get units() {
        const e = this.editedProduct
        return e && e.productApp.ProductUnit
    }

    canDuplicateApp(p: ProductApplicationVm): boolean {
        if (this.readOnly) return false
        if (this.useBiomass) return false
        return (!this.editedProduct || this.editedProduct.isValid()) && (!p.remove)
    }

    canRemoveApp(app: ProductApplicationVm): boolean {
        if (this.readOnly) return false
        if (this.useBiomass) return false
        return true
    }

    editProductIfPossible(p: ProductApplicationVm) {
        if (!((this.isEditing && this.editedProduct !== p) || p.remove)) {
            this.editProduct(p)
        }
    }

    editProduct(p: ProductApplicationVm) {

        this.editedProductBase = p.clone()
        this.editedProduct = p
        this.isEditing = true
        this.ctxt.isEditingProduct = true

        this.editedProduct.syncRates(true)
    }

    duplicateProduct(p: ProductApplicationVm) {
        this.endProductEditing()
        var duplicate = p.clone(true)
        duplicate.productApp.NutritionPlanProductAppId = 0
        duplicate.productApp.ApplicationDate = null
        duplicate.justAdded = true
        this.products.push(duplicate)
        this.editProduct(duplicate)
    }

    canAutoComplete(): boolean {

        const { products, editedProduct: current, readOnly } = this

        if (readOnly) return false

        const apps = products.slice(0)
        apps.remove(a => a === current)

        var hasRate = current.nutrientPct || current.nutrientRate || current.productRate

        return (apps.length === 0 || !apps.find(a => a.targetNutrient === current.targetNutrient &&
            a.applicationDate >= current.applicationDate)) && !hasRate

    }

    endProductEditing(confirm: boolean = false) {
        if (confirm) {
            var otherApplications = this.products.slice(0)
            otherApplications.remove(a => a === this.editedProduct)

            // Check if application have changed and if there is another application after it that is "Completed"
            if (!compareApplicationVms(this.editedProductBase, this.editedProduct) &&
                otherApplications.find(a => a.targetNutrient === this.editedProduct.targetNutrient &&
                    a.applicationDate >= this.editedProduct.applicationDate &&
                    a.autoComplete)) {
                shell.showWarning(this.shell, "You should recalculate the last application you have 'Completed', to make sure it is not overapplied.", "Warning")
            }
        }

        if (this.useBiomass && this.products.length > 0 && (this.editedProduct.targetNutrient !== NutrientType.N || this.products.length > 1)) {
            this.ctxt.useBiomass = false
        }

        if (this.editedProduct && this.editedProduct != null) {
            this.editedProduct.justAdded = false
        }

        this.editedProductBase = null
        this.editedProduct = null
        this.isEditing = false
        this.ctxt.isEditingProduct = false

        this.sortApplications()

        this.ctxt.updateTargetModel()

    }

    cancelProductEditing() {

        if (this.editedProduct.justAdded) {

            this.products.pop()

        } else {

            this.products.remove(p => p === this.editedProduct)
            this.products.push(this.editedProductBase)

        }

        this.endProductEditing()
    }

    removeProduct(p: ProductApplicationVm, cancel?: boolean) {

        if (p.isNew()) {
            this.products.remove(i => i == p)
        }
        else {
            p.remove = !cancel
        }

        this.endProductEditing()

    }

    canCommit(): boolean {
        return this.products && this.products.length && this.isEditing === false && this.products.every(p => p.isValid())
    }

    commit() {

        if (!this.canCommit()) {
            return
        }

        this.ctxt.applications = this.products

        this.closeComponent()

    }

    getIncorporationMethods(product: ProductApplicationVm): IParam[] {

        const { stock } = product
        const manureType = stock?.manureType

        if (manureType) {

            if (manureType.Applications.length) {
                const region = this.svc.holding.Region
                return this.manureAppMethods.filter(p => getManureTypeApp(manureType, p.value, region))
            }

            return this.manureAppMethods

        }

        return []
    }

    getProductUnits(product: ProductApplicationVm) {
        return getProductUnits(product)
    }

    getProductUnitsText(product: ProductApplicationVm, unit: FertiliserAppUnit): string {
        return getProductUnitsText(product, unit)
    }

    getProductUnitsTextNormalized(product: ProductApplicationVm): string {
        return getProductUnitsTextNormalized(product)
    }

    hasOverrides(p: ProductApplicationVm): boolean {
        return (p && p.hasOverrides())
    }

    getAmountHtml(p = this.editedProduct) {

        if (p) {
            const a = p.displayedAmount
            const c = a === undefined
                ? "&ndash;"
                : a.toLocaleString() + " " + p.getUnitsText(false)
            return this.useBiomass
                ? c + "&nbsp;<i class='fas fa-exclamation-triangle fa-xs warning'></i>"
                : c
        }

    }
}
